import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup
} from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NgxCurrencyDirective } from 'ngx-currency';

import { UnitSelectorComponent, UnitSelectorSource } from '@shared/components/unit-selector/unit-selector.component';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { OptionalFieldComponent } from '@shared/components/optional-field/optional-field.component';
import { DivineServiceTypeDto } from '../../models/divine-service.model';
import { OptionalFieldsVisibility } from '../../models/divine-service-state.model';
import { DivineServiceValidationService } from '../../services/validation/divine-service-validation.service';
import { CURRENCY_ARS_CONFIG, CURRENCY_USD_CONFIG } from '@shared/config/currency.config';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Componente reutilizable para los campos del formulario de Servicios Divinos
 * Contiene: Campos requeridos (Fecha, Comunidad, Tipo, Texto Bíblico) 
 * y campos opcionales (Ofrendas, Siervo Responsable, Lugar, Notas)
 */
@Component({
  selector: 'app-divine-service-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    UnitSelectorComponent,
    DatePickerComponent,
    OptionalFieldComponent
  ],
  templateUrl: './divine-service-form.component.html',
  styleUrl: './divine-service-form.component.scss'
})
export class DivineServiceFormComponent implements OnInit, OnDestroy {
  @Input() serviceForm!: FormGroup;
  @Input() set serviceTypes(types: DivineServiceTypeDto[]) {
    this._serviceTypes = types;
    // Seleccionar automáticamente el primer tipo si no hay uno seleccionado
    if (types.length > 0 && !this.serviceForm.get('type')?.value) {
      this.serviceForm.patchValue({ type: types[0].value }, { emitEvent: false });
    }
  }
  get serviceTypes(): DivineServiceTypeDto[] {
    return this._serviceTypes;
  }
  private _serviceTypes: DivineServiceTypeDto[] = [];

  @Input() isLoadingServiceTypes = false;
  @Input() optionalFieldsVisibility: OptionalFieldsVisibility | null = null;

  /**
   * Fuente de datos para el selector de unidades.
   * - `'all'`: búsqueda libre en el backend (modo edición).
   * - `OrganizationUnitItem[]`: lista fija de unidades permitidas (modo creación).
   */
  @Input() unitSource: UnitSelectorSource = 'all';

  @Output() visibilityChange = new EventEmitter<Partial<OptionalFieldsVisibility>>();
  @Output() formValueChange = new EventEmitter<any>();
  @Output() typeChange = new EventEmitter<any>();

  ConfigARS = CURRENCY_ARS_CONFIG;
  ConfigUSD = CURRENCY_USD_CONFIG;

  private destroy$ = new Subject<void>();

  get visibility(): OptionalFieldsVisibility {
    return this.optionalFieldsVisibility || {
      memberInCharge: false,
      location: false,
      notes: false,
      offerings: false,
      visits: false,
      newSouls: false
    };
  }

  constructor(
    private validationService: DivineServiceValidationService
  ) {}

  ngOnInit(): void {
    // Escuchar cambios en el tipo de servicio para actualizar validadores de ofrendas
    this.serviceForm.get('type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedTypeId => {
        this.validationService.updateOfferingValidators(
          this.serviceForm,
          selectedTypeId,
          this.serviceTypes
        );
        this.typeChange.emit(selectedTypeId);
      });

    // Emitir cambios cuando el formulario cambia
    this.serviceForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(values => {
        this.formValueChange.emit(values);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
